/**
 * HeadWithdraw 타입 정의 (본사 입출금현황)
 */


/** 입출금 구분 타입 */
export type HeadTransactionType = 'deposit' | 'withdrawal' | 'point';

/** 외부 출금 상태 */
export type ExternalWithdrawalStatus =
    | 'ACCEPTED'
    | 'PROCESSING'
    | 'PENDING_CONFIRMATION'
    | 'SUCCEEDED'
    | 'FAILED';

/** 외부 출금 내역 DTO (백엔드 응답) */
export type ExternalWithdrawalDto = {
    seqNo: number;
    requestId: string;
    withdrawalId: string;
    transferId: string;
    status: ExternalWithdrawalStatus;
    isFinal: boolean;
    transferAmount: number;
    bankCode: string;
    accountNumber: string;
    accountMemo?: string;
    mchtTrdNo: string;
    hectoTrdNo?: string;
    resultCode?: string;
    resultMessage?: string;
    completedAt?: string;
    insDate: string;
    insUser?: string;
};

/** 외부 출금 내역 목록 조회 응답 DTO */
export type GetExternalWithdrawalsResponseDto = {
    list: ExternalWithdrawalDto[];
    totalCount: number;
    totalAmount: number;
};

/** useHeadWithdrawData 훅 파라미터 타입 */
export type UseHeadWithdrawDataParams = {
    supplierId?: string;
};

/** 본사 입출금 내역 Domain Model */
export type HeadWithdrawHistory = {
    no: number;
    processDateTime: string;
    transactionType: HeadTransactionType;
    acctType: string; // 원본 acctType 저장 (RP, RW, SW, D, W, C)
    acctTypeName?: string; // 구분명 (백엔드에서 매핑된 이름)
    amount: number;
    suppId?: string; // 협력사ID
    suppName?: string; // 협력사명
    riderId?: string; // 라이더ID
    riderName?: string; // 라이더명
    reqType?: string;   // 요청자 타입 (S: 협력사, R: 라이더, C: 본사) - ACCT_TARGET
    reqTypeName?: string; // 주체구분 (본사, 협력사, 라이더)
    withdrawAmt?: number; // 출금 금액
    feeAmt?: number; // 수수료 금액
    balAmt?: number; // 보유잔액
    balance?: number; // 예치금잔액
    depositDate?: string; // 입금일자 (입금일 때만)
    depositTime?: string; // 입금시간 (입금일 때만)
    depositAmt?: number; // 입금요청금액 (입금일 때만)
    cancelTime?: string; // 취소시간 (입금취소일 때만)
    withdrawDate?: string; // 출금 거래일자 (출금일 때만) - yyyy-MM-dd
    withdrawTime?: string; // 출금시간 (출금일 때만)
    prevBalAmt?: number; // 재등록 전 잔액 (RR/SR 타입일 때만)
    acctDate?: string; // 정산일자 (yyyyMMdd 형식) - 정산 대상 주차
    briefs?: string; // 적요 (T 타입일 때만)
};

/** 본사 입출금내역 DTO (백엔드 응답) */
export type HeadWithdrawHistoryDto = {
    seqNo: number;
    insDate: string;
    acctType: string; // RP, RW, SW, D, W, C 등
    acctTypeName?: string; // 구분명 (백엔드에서 매핑된 이름)
    tranAmt: number;
    suppId?: string | null; // 협력사ID
    suppName?: string | null; // 협력사명
    riderId?: string | null; // 라이더ID
    riderName?: string; // 라이더명
    reqTypeName?: string; // 주체구분 (본사, 협력사, 라이더)
    reqType?: string; // 요청자 타입 (S: 협력사, R: 라이더)
    reqName?: string; // 요청자 이름
    withrawAmt?: number; // 입출금 금액 (API 필드명)
    feeAmt?: number; // 수수료 금액
    balAmt?: number; // 보유잔액
    balance?: number; // 예치금잔액
    depositDate?: string; // 입금일자 (입금일 때만) - vacs_ahst.TR_IL
    depositTime?: string; // 입금시간 (입금일 때만) - vacs_ahst.TR_SI
    depositAmt?: number; // 입금요청금액 (입금일 때만) - vacs_ahst.TR_AMT
    cancelTime?: string; // 취소시간 (입금취소일 때만)
    withdrawDate?: string; // 출금 거래일자 (출금일 때만) - yyyy-MM-dd
    withdrawTime?: string; // 출금시간 (출금일 때만)
    prevBalAmt?: number; // 재등록 전 잔액 (RR/SR 타입일 때만)
    acctDate?: string; // 정산일자 (yyyyMMdd 형식) - 정산 대상 주차
    briefs?: string; // 적요 (T 타입일 때만)
};

/** 본사 입출금내역 목록 조회 응답 DTO */
export type GetHeadWithdrawHistoryListResponseDto = {
    list: HeadWithdrawHistoryDto[];
    totalCount: number;
    depositSum: number;      // 입금 합계
    withdrawalSum: number;   // 출금 합계
    feeSum: number;         // 수수료 합계
    totalSum: number;       // 최종 합계 (입금 - 출금 - 수수료)
    currentBalanceByRider?: Record<string, number>;   // 라이더별 현재 잔액 (riderId -> balance)
    currentBalanceBySupplier?: Record<string, number>; // 협력사별 현재 잔액 (suppId -> balance)
};

/** 출금 가능 금액 조회 응답 DTO */
export type HeadGetWithdrawableAmountResponseDto = {
    balAmt: number;
    unpaidAmt: number;
    insDate: number[] | null;
    bankCode: string;
    bankName: string;
    acctNum: string;
    acctName: string;
    // 입금 계좌 정보
    depositBankCode?: string;
    depositBankName?: string;
    depositAcctNum?: string;
    depositAcctName?: string;
    // 출금 계좌 정보
    withdrawBankCode?: string;
    withdrawBankName?: string;
    withdrawAcctNum?: string;
    withdrawAcctName?: string;
};

/** 출금 신청 요청 DTO */
export type HeadRequestWithdrawRequestDto = {
    suppId: string;
    reqVal: number;
    briefs?: string;
    insertUserId?: string; // 사용자ID
};

/** 출금 신청 응답 DTO */
export type HeadRequestWithdrawResponseDto = {
    success: boolean;
    message: string;
    requestId?: string;
};

/** 입금 신청 요청 DTO */
export type HeadRequestDepositRequestDto = {
    userType: string;
    reqVal: number;
    briefs?: string;
};

/** 입금 신청 응답 DTO */
export type HeadRequestDepositResponseDto = {
    success: boolean;
    message: string;
    requestId?: string;
};

/** 출금 신청 내역 DTO */
export type HeadWithdrawRequestDetailDto = {
    requestId: string;
    requestAmount: number;
    systemFee: number;
    transferFee: number;
    expectedAmount: number;
    accountInfo: string;
    requestDate: string;
    status: 'pending' | 'approved' | 'rejected' | 'completed';
};

/** WithdrawModal Props */
export type HeadWithdrawModalProps = {
    withdrawableAmount: HeadGetWithdrawableAmountResponseDto | null;
    withdrawFee?: number; // 출금수수료
    accountInfo?: {
        depositBankCode?: string;
        depositAcctNum?: string;
        depositAcctName?: string;
        withdrawBankCode?: string;
        withdrawAcctNum?: string;
        withdrawAcctName?: string;
    } | null;
    onClose: () => void;
    onRequest: (
        amount: number,
        memo?: string
    ) => Promise<void>;
};

/** DepositModal Props */
export type HeadDepositModalProps = {
    withdrawableAmount: HeadGetWithdrawableAmountResponseDto | null;
    depositFee?: number; // 입금수수료
    accountInfo?: {
        depositBankCode?: string;
        depositAcctNum?: string;
        depositAcctName?: string;
        withdrawBankCode?: string;
        withdrawAcctNum?: string;
        withdrawAcctName?: string;
    } | null;
    onClose: () => void;
    onRequest: (
        amount: number,
        memo?: string
    ) => Promise<void>;
};

/** DTO -> Domain 변환 */
export const headWithdrawHistoryFromDto = (
    dto: HeadWithdrawHistoryDto,
    index: number
): HeadWithdrawHistory => {
    let transactionType: HeadTransactionType;
    if (dto.acctType === 'RP' || dto.acctType === 'RW' || dto.acctType === 'SW' || dto.acctType === 'RR' || dto.acctType === 'SR') {
        transactionType = 'point';
    } else if (dto.acctType === 'D') {
        transactionType = 'deposit';
    } else if (dto.acctType === 'W') {
        transactionType = 'withdrawal';
    } else if (dto.acctType === 'TI' || dto.acctType === 'T') {
        // 송금(입금)과 본사입금도 입금으로 처리
        transactionType = 'deposit';
    } else {
        // 기본값: 입금
        transactionType = 'deposit';
    }

    // reqType에 따라 협력사명과 라이더명 설정
    let suppName: string | undefined;
    let riderId: string | undefined;
    let riderName: string | undefined;

    if (dto.reqType === 'S') {
        // 협력사인 경우: reqName을 협력사명에 표시
        suppName = dto.reqName;
        riderId = undefined;
        riderName = undefined;
    } else if (dto.reqType === 'R') {
        // 라이더인 경우: suppName을 협력사명에, reqName을 라이더명에 표시
        suppName = dto.suppName || undefined;
        riderId = dto.riderId || undefined;
        riderName = dto.reqName;
    } else {
        // 기존 로직 유지 (reqType이 없는 경우)
        suppName = dto.suppName || undefined;
        riderId = dto.riderId || undefined;
        riderName = dto.riderName;
    }

    // 금액 처리: 모든 타입 부호 유지
    const amount = dto.tranAmt || 0;

    return {
        no: dto.seqNo || index + 1,
        processDateTime: dto.insDate || '',
        transactionType,
        acctType: dto.acctType,
        acctTypeName: dto.acctTypeName, // 백엔드에서 매핑된 구분명 사용
        amount: amount,
        suppId: dto.suppId || undefined, // 협력사ID
        suppName,
        riderId,
        riderName,
        reqType: dto.reqType,   // ACCT_TARGET (S: 협력사, R: 라이더)
        reqTypeName: dto.reqTypeName,
        withdrawAmt: dto.withrawAmt, // API의 withrawAmt를 withdrawAmt로 매핑
        feeAmt: dto.feeAmt,
        balAmt: dto.balAmt, // 보유잔액
        balance: dto.balance, // 예치금잔액
        depositDate: dto.depositDate, // 입금일자
        depositTime: dto.depositTime, // 입금시간
        depositAmt: dto.depositAmt, // 입금요청금액
        cancelTime: dto.cancelTime, // 취소시간
        withdrawDate: dto.withdrawDate, // 출금 거래일자
        withdrawTime: dto.withdrawTime, // 출금시간
        prevBalAmt: dto.prevBalAmt, // 재등록 전 잔액 (RR/SR 타입일 때만)
        acctDate: dto.acctDate, // 정산일자 (정산 대상 주차)
        briefs: dto.briefs // 적요 (T 타입일 때만)
    };
};

/** 목록 변환 */
export const headWithdrawHistoryListFromDto = (
    response: GetHeadWithdrawHistoryListResponseDto
): HeadWithdrawHistory[] => {
    if (!response || !response.list || !Array.isArray(response.list)) {
        return [];
    }
    return response.list.map((dto, index) =>
        headWithdrawHistoryFromDto(dto, index)
    );
};
